import { useCallback, useState } from 'react';
import { toast } from '../../../common/toast';
import { uploadPhoto, deletePhoto } from './photoService';
import { photoCache } from './photoCache';
import axiosInstance from '../../../fetchConfig/axiosConfig';
import logger from '../../../utils/logger';

// Shared upload / set-primary / delete flow for a single parcel's photo. Every mutation goes
// through photoCache.invalidate so all mounted components showing this parcel_id (a grid cell,
// the map's PropertyRecordCard, etc.) pick up the change — see photoCache.invalidate's docstring.
export const usePhotoActions = (parcelId, onPhotoUpdate) => {
  const [uploading, setUploading] = useState(false);

  const runUpload = useCallback(async (fileOrSourceUrl, successMessage) => {
    setUploading(true);
    try {
      const newUrl = await uploadPhoto(fileOrSourceUrl, parcelId, axiosInstance);
      photoCache.invalidate(parcelId, newUrl);
      onPhotoUpdate?.(newUrl, parcelId);
      toast.success(successMessage);
      return newUrl;
    } catch (error) {
      toast.error(error.message || 'Failed to update photo');
      return null;
    } finally {
      setUploading(false);
    }
  }, [parcelId, onPhotoUpdate]);

  const uploadNewPhoto = useCallback((file) => runUpload(file, 'Photo uploaded successfully'), [runUpload]);

  const setPrimaryPhoto = useCallback((sourceUrl) => runUpload(sourceUrl, 'Primary photo updated successfully'), [runUpload]);

  const removePhoto = useCallback(async () => {
    try {
      await deletePhoto(parcelId, axiosInstance);
      photoCache.invalidate(parcelId, null);
      onPhotoUpdate?.('', parcelId);
      toast.success('Photo deleted successfully');
    } catch (error) {
      logger.error('Error deleting photo:', error);
      toast.error('Failed to delete photo');
    }
  }, [parcelId, onPhotoUpdate]);

  return { uploading, uploadNewPhoto, setPrimaryPhoto, removePhoto };
};
